import type {
  Device,
  FetchDeviceListParams
} from 'Services/ServiceProviderManagement/Features/Device/types'
import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  DEVICE = 'Device'
}

export const deviceApi = createBaseApi({
  reducerPath: 'deviceApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.DEVICE]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getDevices: builder.query<ListResponse<Device>, FetchDeviceListParams | void>({
      query: params => ({
        url: '/api/v1/device/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ device_id }) => ({ type: TagTypes.DEVICE, id: device_id }) as const
              ),
              { type: TagTypes.DEVICE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.DEVICE, id: RTKCacheTags.LIST }]
    }),
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getAllDevices: builder.query<ListResponse<Device>, FetchDeviceListParams | void>({
      query: (params = {}) => ({
        url: '/api/v1/device/',
        params: {
          ...params,
          bypass_pagination: true
        }
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ device_id }) => ({ type: TagTypes.DEVICE, id: device_id }) as const
              ),
              { type: TagTypes.DEVICE, id: RTKCacheTags.LIST_ALL }
            ]
          : [{ type: TagTypes.DEVICE, id: RTKCacheTags.LIST_ALL }]
    }),
    getSingleDevice: builder.query<Device, string>({
      query: id => `/api/v1/device/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.DEVICE, id }]
    }),
    createSingleDevice: builder.mutation<Device, Partial<Device>>({
      query: data => ({
        url: `/api/v1/device/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST },
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleDevice: builder.mutation<Device, { id: string } & Partial<Device>>({
      query: data => {
        const { id, ...fields } = data
        const updatedFields = {
          ...fields,
          // activity_end_date has to be null when updating passive device
          activity_end_date:
            !fields.active && fields.activity_end_date ? null : fields.activity_end_date
        }
        return {
          url: `/api/v1/device/${id}/`,
          method: 'PUT',
          body: updatedFields
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.DEVICE, id },
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST },
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleDevice: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/device/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST },
        { type: TagTypes.DEVICE, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetDevicesQuery,
  useGetAllDevicesQuery,
  useGetSingleDeviceQuery,
  useCreateSingleDeviceMutation,
  useUpdateSingleDeviceMutation,
  useDeleteSingleDeviceMutation
} = deviceApi
